import { Component, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { EmailShare } from "@tylertech/shared-module";
import { IShellService } from "@tylertech/incode-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { ExecutionResult, ICardAction, ICardData } from "@tylertech/tyler-api-types";
import { Command, LabelCommand } from "@tylertech/commands-module";
export interface ServiceRequestTextEmailDialogData
{
    readonly cardAction: ICardAction;
    readonly cardData: ICardData;
    readonly isAccepted: boolean;
    readonly requestId: string;
    readonly requestType: string;
}
const enum NotificationMethod
{
    None = "none",
    Text = "text",
    Email = "email",
}
@Component({
    selector: "service-request-text-email-dialog-component",
    templateUrl: "./serviceRequestTextEmailDialog.tmpl.html",
    styleUrls: ["./serviceRequestTextEmailDialog.component.scss"],
    standalone: false
})
export class ServiceRequestTextEmailDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<ServiceRequestTextEmailDialogComponent, boolean>>(MatDialogRef);
    private readonly data = inject<ServiceRequestTextEmailDialogData>(MAT_DIALOG_DATA);
    private readonly $shellService = inject(IShellService);
    private commonDialogsService = inject(CommonDialogsService);

    readonly commands: ReadonlyArray<Command>;
    private canEmail: boolean;
    private canText: boolean;
    subject: string;
    message: string;
    constructor()
    {
        this.commands = [
            new LabelCommand({
                label: "Text",
                action: () => this.textRequest(),
                getEnabled: () => this.canText,
                tooltip: undefined,
                getVisible: () => this.canText,
            }),
            new LabelCommand({
                label: "Email",
                action: () => this.emailRequest(),
                getEnabled: () => this.canEmail,
                tooltip: undefined,
                getVisible: () => this.canEmail,
            }),
            new LabelCommand({
                label: "None",
                action: () => this.dialogRef.close(true),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Cancel",
                action: () => this.dialogRef.close(false),
                getEnabled: () => true,
            }),
        ];
    }

    private get cardData(): ICardData
    {
        return this.data.cardData;
    }

    private get cardAction(): ICardAction
    {
        return this.data.cardAction;
    }

    private get requestId(): string
    {
        return this.data.requestId;
    }

    private get requestType(): string
    {
        return this.data.requestType;
    }

    private get isAccepted(): boolean
    {
        return this.data.isAccepted;
    }

    ngOnInit(): void
    {
        this.subject = `${this.requestType} Service Request ${this.isAccepted ? "Accepted" : "Rejected"}`;
        this.message = this.getField("EmailBody");
        const notificationMethod = this.getField("Notification Method")?.toLowerCase() || "";
        switch (notificationMethod)
        {
            case NotificationMethod.Text:
                this.canText = true;
                this.canEmail = false;
                break;
            case NotificationMethod.Email:
                this.canEmail = true;
                this.canText = false;
                break;
            case NotificationMethod.None:
                this.canEmail = false;
                this.canText = false;
                break;
            default:
                break;
        }
    }

    private getField(fieldName: string): string | undefined
    {
        return this.cardData.DataElements.find(x => x.Label === fieldName)?.Value;
    }

    private emailRequest(onlyEmail: boolean = true): void
    {
        EmailShare.send({
            recipient: this.getField("Email"),
            subject: this.subject,
            message: this.getField("EmailBody"),
        });
        if (onlyEmail)
        {
            this.dialogRef.close(true);
        }
    }

    private textRequest(): void
    {
        const json = JSON.stringify(
            this.requestType === "Disconnect"
                ? {
                    RequestId: this.requestId,
                    RequestType: this.requestType,
                    AccountNumber: this.getField("Account Number"),
                    PhoneNumber: this.getField("Phone Number"),
                    Message: `${this.subject}\n\n${this.getField("EmailBody")}`,
                    IsAccepted: String(this.isAccepted),
                }
                : {
                    RequestId: this.requestId,
                    RequestType: this.requestType,
                    PhoneNumber: this.getField("Phone Number"),
                    Message: `${this.subject}\n\n${this.getField("EmailBody")}`,
                    IsAccepted: String(this.isAccepted),
                }
        );
        this.$shellService.executeShellFunction(this.cardAction.content, json, false).then(
            (result: ExecutionResult<string>) =>
            {
                // TODO - We are already in a dialog context... DO NOT OPEN SECONDARY DIALOGS
                this.commonDialogsService
                    .showAlertHtmlMessage(result.Result, "Success", "Success")
                    .then(() => this.dialogRef.close(true));
            },
            (result: ExecutionResult<string>) =>
            {
                // TODO - We are already in a dialog context... DO NOT OPEN SECONDARY DIALOGS
                this.commonDialogsService
                    .showAlertHtmlMessage(result.Result, "Error", "Error")
                    .then(() => this.dialogRef.close(false));
            }
        );
    }
}
